import './DetailsTickets.scss';
import { Button, Icon, Checkbox, Card } from '@design-system/index';
import { ArchiveLine, ArchiveFilled, UnarchiveFilled, UnarchiveLine } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate } from '@utils/dates';
import { formatMoney } from '@utils/numbers';
import { useDetailsTicketsVM } from './useDetailsTicketsVM';
import { TicketingTable } from '@services/member/ticketing/ticketing.types';

export default function DetailsTickets() {
    const {
        handleArchiveTicketing,
        navigate,
        columnHelper,
        isArchived,
        setIsArchived,
        mutationHook,
        needReload,
        id
    } = useDetailsTicketsVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor((row) => row.ticketType.label, {
            id: 'ticketType.label',
            header: 'Type de billet',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.ticketingAt, {
            id: 'ticketingAt',
            header: 'Date de vente',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{formatDate(info.getValue())}</>
        }),
        columnHelper.accessor((row) => row.saleCount, {
            id: 'saleCount',
            header: 'Nombre de billets',
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.saleAmount, {
            id: 'saleAmount',
            header: 'Montant total',
            meta: {
                clickable: true
            },
            cell: (info) => <>{formatMoney(info.getValue())}</>
        }),
        columnHelper.accessor((row) => `${row.firstTicketNumber} à ${row.lastTicketNumber}`, {
            id: 'firstTicketNumber',
            header: 'Numéros des billets',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>,
        }),
        columnHelper.display({
            id: 'archive',
            size: 0,
            header: isArchived ? 'Désarchiver' : 'Archiver',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={
                                <Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />
                            }
                            icon={
                                <Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />
                            }
                            onClick={() => handleArchiveTicketing([info.row.original], !isArchived)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<TicketingTable>[];

    return (
        <div className="details-member-container">
            <Card headerType="very-light" headerTitle="Liste des billets vendus">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    createButtonOnClick={() => navigate(`/members-management/ticketings/add-multi?memberId=${id}`)}
                    createButtonLabel="Nouvelle vente"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    needReload={needReload}
                    mutationHook={mutationHook}
                    mutationHookParams={{ id: id }}
                    onRowClick={(row) => navigate(`/members-management/ticketings/${row.id}/edit`)}
                    defaultSorting={{
                        field: 'ticketingAt',
                        direction: 'desc'
                    }}
                    deleting={{
                        enabled: isArchived,
                        entity: 'ticketing',
                    }}
                    className="no-card"
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? (
                                    <Icon icon={<UnarchiveLine />} size={20} color="primary" />
                                ) : (
                                    <Icon icon={<ArchiveLine />} size={20} color="primary" />
                                ),
                                hoverIcon: isArchived ? (
                                    <Icon icon={<UnarchiveFilled />} size={20} color="primary" />
                                ) : (
                                    <Icon icon={<ArchiveFilled />} size={20} color="primary" />
                                ),
                                onClick: (lines: TicketingTable[]) => handleArchiveTicketing(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </Card>
        </div>
    );
}
